import { EiTableColumn, EiTableRow } from '../models/ei-table.model'
import {
    applyColumnSearch,
    applyGlobalSearch,
    applySelectFilters,
    applySort,
} from './ei-table.data.helpers'
import { getManageableColumnKeys, getMandatoryColumnKeys } from './ei-table.column.helpers'
import { safeSetLocalStorage } from './ei-table.state.helpers'
import { EiTableSortState } from '../models/ei-table.model'

/**
 * Get row ID from a row object using the specified rowIdKey.
 * Falls back to JSON.stringify if the value is not a string or number.
 */
export function getRowId(row: EiTableRow, rowIdKey: string): string | number {
    const rowValue = (row as unknown as Record<string, unknown>)[rowIdKey]
    if (typeof rowValue === 'string' || typeof rowValue === 'number') {
        return rowValue
    }
    return JSON.stringify(rowValue ?? row)
}

/**
 * Apply all filters and sorting to rows.
 * Returns filtered and sorted rows ready for pagination.
 */
export function applyFiltersAndSort(
    rows: EiTableRow[],
    visibleColumns: EiTableColumn[],
    globalSearch: string,
    columnSearch: Record<string, string>,
    selectFilters: Record<string, string | number | (string | number)[] | null>,
    sort: EiTableSortState,
): EiTableRow[] {
    const afterGlobalSearch = applyGlobalSearch(rows, visibleColumns, globalSearch)
    const afterColumnSearch = applyColumnSearch(afterGlobalSearch, visibleColumns, columnSearch)
    const afterFilters = applySelectFilters(afterColumnSearch, visibleColumns, selectFilters)
    return applySort(afterFilters, visibleColumns, sort)
}

/**
 * Handle visible columns change event.
 * Validates selection, includes mandatory columns, and persists to storage.
 */
export function handleVisibleColumnsChange(
    values: (string | number)[] | string | number | null,
    allColumns: EiTableColumn[],
    viewId: string,
): {
    finalKeys: string[]
    shouldResetPage: boolean
} {
    const manageableDefaults = getManageableColumnKeys(allColumns)
    const mandatoryKeys = getMandatoryColumnKeys(allColumns)
    const allowedKeys = new Set(manageableDefaults)

    let selectedKeys = (Array.isArray(values) ? values : values ? [values] : [])
        .map(String)
        .filter((key) => allowedKeys.has(key))

    // If user clears all, default back to all manageable columns
    if (!selectedKeys.length) {
        selectedKeys = manageableDefaults
    }

    // Always include mandatory columns
    const finalKeys = Array.from(new Set([...mandatoryKeys, ...selectedKeys]))

    // Persist to storage
    safeSetLocalStorage(`ei-table:${viewId}:visibleCols`, finalKeys)

    return { finalKeys, shouldResetPage: true }
}

/**
 * Handle page size change event.
 * Validates and persists new page size to storage.
 */
export function handlePageSizeChange(
    value: string | number | (string | number)[] | null,
    currentPageSize: number,
    viewId: string,
): { newPageSize: number | null; shouldResetPage: boolean } {
    const normalizedPageSize = typeof value === 'number' ? value : Number(value)

    if (!Number.isFinite(normalizedPageSize) || normalizedPageSize <= 0) {
        const fallbackPageSize = 10

        if (fallbackPageSize === currentPageSize) {
            return { newPageSize: null, shouldResetPage: false }
        }

        safeSetLocalStorage(`ei-table:${viewId}:pageSize`, fallbackPageSize)
        return { newPageSize: fallbackPageSize, shouldResetPage: true }
    }

    if (normalizedPageSize === currentPageSize) {
        return { newPageSize: null, shouldResetPage: false }
    }

    safeSetLocalStorage(`ei-table:${viewId}:pageSize`, normalizedPageSize)

    return { newPageSize: normalizedPageSize, shouldResetPage: true }
}
